import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import type { User, LoginRequest, RegisterRequest } from '../types'
import * as api from '../api/client'

// Role constants (must match backend)
export const Roles = {
  ADMIN: 'admin',
  DEVELOPER: 'developer',
  VIEWER: 'viewer',
} as const

export type Role = typeof Roles[keyof typeof Roles]

// Permission helpers
export const isAdmin = (role?: string): boolean => role === Roles.ADMIN
export const isDeveloper = (role?: string): boolean =>
  role === Roles.DEVELOPER || role === Roles.ADMIN
export const isViewer = (role?: string): boolean =>
  role === Roles.VIEWER || role === Roles.DEVELOPER || role === Roles.ADMIN

// Permission check functions
export const canCreateProject = (role?: string): boolean => isDeveloper(role)
export const canManageTemplates = (role?: string): boolean => isAdmin(role)
export const canManageSettings = (role?: string): boolean => isAdmin(role)
export const canAccessDispatcher = (role?: string): boolean => isAdmin(role)
export const canAccessWatcher = (role?: string): boolean => isAdmin(role)

interface Permissions {
  isAdmin: boolean
  isDeveloper: boolean
  isViewer: boolean
  canCreateProject: boolean
  canManageTemplates: boolean
  canManageSettings: boolean
  canAccessDispatcher: boolean
  canAccessWatcher: boolean
}

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  permissions: Permissions
  login: (data: LoginRequest) => Promise<void>
  register: (data: RegisterRequest) => Promise<User>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Check if user is already authenticated on mount
  useEffect(() => {
    const initAuth = async () => {
      if (api.isAuthenticated()) {
        try {
          const currentUser = await api.getCurrentUser()
          setUser(currentUser)
        } catch {
          // Token invalid or expired, clear it
          api.clearTokens()
        }
      }
      setIsLoading(false)
    }
    initAuth()
  }, [])

  // The API client signals an unrecoverable 401; drop the user so protected
  // routes send them to login.
  useEffect(() => {
    const onExpired = () => setUser(null)
    window.addEventListener(api.AUTH_EXPIRED_EVENT, onExpired)
    return () => window.removeEventListener(api.AUTH_EXPIRED_EVENT, onExpired)
  }, [])

  // After login/register, check if there's an anonymous project to claim
  const claimPendingProject = useCallback(async () => {
    const projectId = sessionStorage.getItem('pending_claim_project')
    if (projectId) {
      sessionStorage.removeItem('pending_claim_project')
      try {
        await api.claimProject(projectId)
      } catch (e) {
        // Non-fatal: project may already be claimed or deleted
        console.warn('Failed to claim project:', e)
      }
    }
  }, [])

  const login = useCallback(async (data: LoginRequest) => {
    const response = await api.login(data)
    setUser(response.user)
    await claimPendingProject()
  }, [claimPendingProject])

  const register = useCallback(async (data: RegisterRequest): Promise<User> => {
    const response = await api.register(data)
    setUser(response.user)
    await claimPendingProject()
    return response.user
  }, [claimPendingProject])

  const logout = useCallback(async () => {
    await api.logout()
    setUser(null)
  }, [])

  const refreshUser = useCallback(async () => {
    if (api.isAuthenticated()) {
      try {
        const currentUser = await api.getCurrentUser()
        setUser(currentUser)
      } catch {
        api.clearTokens()
        setUser(null)
      }
    }
  }, [])

  // Compute permissions based on user role
  const permissions = useMemo<Permissions>(() => {
    const role = user?.role
    return {
      isAdmin: isAdmin(role),
      isDeveloper: isDeveloper(role),
      isViewer: isViewer(role),
      canCreateProject: canCreateProject(role),
      canManageTemplates: canManageTemplates(role),
      canManageSettings: canManageSettings(role),
      canAccessDispatcher: canAccessDispatcher(role),
      canAccessWatcher: canAccessWatcher(role),
    }
  }, [user?.role])

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        permissions,
        login,
        register,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
